import { Dispatch, SetStateAction } from "react";
import { useParams } from "react-router-dom";
import { Avatar, Box, Button, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { colors } from "../../../../../theme/palette";
import { NoMembersInGroupFigure } from "../../../../../assets/icons/MUI/GroupsIcons";
import { UserInfo } from "../../../../../types";
import { useProfileData } from "../../../../../hooks/useProfileData";
import { useUpdateGroup } from "../../../../../api/queries/groups/useGroupsMutations";
import { formatImgUrl } from "../../../../../utils/cardFormating";

import PreviewUserCard, { PreviewUserCardLoader } from "./PreviewUserCard";

const GroupPreview = ({
  data,
  isDataLoading,
  membersList,
  setMembersList,
  handleRemoveUser,
  handleUploadImg,
  imagePrev,
  imageName,
}: {
  data: {
    groupMedia?: string;
    groupName?: string;
    groupDescription?: string;
  };
  isDataLoading: boolean;
  membersList: UserInfo[];
  setMembersList: Dispatch<SetStateAction<UserInfo[]>>;
  handleRemoveUser: (user: UserInfo | null) => void;
  handleUploadImg: () => Promise<string | undefined>;
  imagePrev?: string;
  imageName?: string;
}) => {
  const { groupMedia, groupName, groupDescription } = data;
  const { t } = useTranslation();
  const { data: ownProfileData } = useProfileData();
  const { entityId } = ownProfileData;
  const { groupId } = useParams();

  const { mutate: saveGroup, isPending: isSavingGroup } = useUpdateGroup();

  const handleSaveGroup = async () => {
    await handleUploadImg();

    const body = {
      name: groupName,
      description: groupDescription,
      imageLink: formatImgUrl(imageName),
      userIds: membersList.map((user: UserInfo) => user.id),
    };

    console.log("handleSaveGroup | body", body);

    if (!entityId || !groupId) return;
    saveGroup({ entityId, groupId, body });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "12px",
        background: colors.White,
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        width: "300px",
        minHeight: "800px",
        height: "fit-content",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Typography
        variant="body16ExtraBold"
        sx={{
          display: "block",
          paddingBottom: "21px",
          borderBottom: "1px solid #EAECF0",
          marginBottom: "32px",
        }}
      >
        {t("pages.entity.groups.previewGroup")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "47px",
        }}
      >
        {isDataLoading ? (
          <Skeleton variant="circular" width={76} height={76} sx={{ marginBottom: "16px" }} />
        ) : groupMedia || imagePrev ? (
          <Avatar
            src={imagePrev || groupMedia}
            sx={{
              width: "76px",
              height: "76px",
              marginBottom: "16px",
              objectFit: "contain",
            }}
          />
        ) : (
          <Box
            component="span"
            sx={{
              width: "76px",
              height: "76px",
              background: colors.Gray50,
              marginBottom: "16px",
              borderRadius: "50%",
            }}
          />
        )}

        {isDataLoading ? (
          <Skeleton variant="text" width={139} height={20} sx={{ marginBottom: "20px" }} />
        ) : groupName ? (
          <Typography variant="body13Bold" sx={{ marginBottom: "20px", textAlign: "center" }}>
            {groupName}
          </Typography>
        ) : (
          <Box
            component="span"
            sx={{
              marginBottom: "20px",
              width: "139px",
              height: "15px",
              borderRadius: "7.5px",
              background: colors.Gray50,
            }}
          />
        )}
        {isDataLoading ? (
          <Skeleton variant="rectangular" width={196} height={60} sx={{ marginBottom: "20px" }} />
        ) : groupDescription ? (
          <Typography
            variant="body13Regular"
            sx={{
              marginBottom: "20px",
              textAlign: "center",
              wordBreak: "break-word",
              hyphens: "auto",
              maxWidth: "100%",
            }}
          >
            {groupDescription}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              rowGap: "6px",
            }}
          >
            <Box
              component="span"
              sx={{
                width: "196px",
                height: "4px",
                borderRadius: "7.5px",
                background: colors.Gray50,
              }}
            />
            <Box
              component="span"
              sx={{
                width: "192px",
                height: "4px",
                borderRadius: "7.5px",
                background: colors.Gray50,
              }}
            />
            <Box
              component="span"
              sx={{
                width: "168px",
                height: "4px",
                borderRadius: "7.5px",
                background: colors.Gray50,
              }}
            />
          </Box>
        )}
      </Box>
      <Typography
        variant="body13Bold"
        sx={{
          display: "block",
          marginBottom: "32px",
        }}
      >
        {!isDataLoading && `${t("pages.entity.groups.groupMembers")} (${membersList.length})`}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", justifyContent: "space-between", flex: 1 }}>
        {isDataLoading ? (
          [...Array(3)].map((_, idx) => <PreviewUserCardLoader key={idx} isLast={idx === 2} />)
        ) : membersList.length === 0 ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flex: 1,
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              rowGap: "22px",
            }}
          >
            <NoMembersInGroupFigure />
            <Typography variant="body13Regular" sx={{ maxWidth: "118px", color: colors.Dark600 }}>
              {t("pages.entity.groups.selectedMembersView")}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flex: 1,
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "center",
              rowGap: "22px",
              marginBottom: "30px",
            }}
          >
            {membersList?.map((user: UserInfo, index: number) => (
              <PreviewUserCard
                key={user.id}
                user={user}
                isLast={membersList.length === index + 1}
                handleRemoveUser={handleRemoveUser}
              />
            ))}
          </Box>
        )}
        <Button
          variant="primary-sm"
          disabled={isSavingGroup || !groupName || membersList.length === 0}
          fullWidth
          sx={{
            marginTop: "auto",
          }}
          onClick={handleSaveGroup}
        >
          {t("pages.entity.groups.saveGroup")}
        </Button>
      </Box>
    </Box>
  );
};

export default GroupPreview;
